import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, message} from 'antd';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';
import ParcelsDownloadModal from '../../central/parcels/components/ParcelsDownloadModal';
import {useDownloadCooldown} from '../../../hooks/useDownloadCooldown';
import {getUserInfo} from '../../../utils/auth';
import ReviewTaskFilter, {
    ReviewTaskFilters,
    getDefaultReviewTaskFilters,
} from './components/ReviewTaskFilter';
import ReviewTaskTable from './components/ReviewTaskTable';
import ReviewTaskDetailDrawer from './components/ReviewTaskDetailDrawer';
import ReviewTaskTabCards from './components/ReviewTaskTabCards';
import {useReviewTaskQuery} from './hooks/useReviewTaskQuery';
import {useReviewTaskTabCounts} from './hooks/useReviewTaskTabCounts';
import {useReviewTaskCanOperate} from './hooks/useReviewTaskPermission';
import {
    GENERATE_REVIEW_TASK_DOWNLOAD_REPORT,
    GenerateReviewTaskDownloadReportResponse,
    GenerateReviewTaskDownloadReportVariables,
} from './graphql/mutations/reviewTaskMutations';
import {
    ReviewBizType,
    ReviewTaskFiltersTypeInput,
    ReviewTaskListItem,
} from './graphql/types/ReviewTaskType';

const DEFAULT_PAGE_SIZE = 20;
const BIZ_TYPE = ReviewBizType.CUSTOMER_COMPLAINT;

/** UI 层 filters 转 GraphQL filters 非 status 部分（卡片与列表共享） */
const buildSharedFiltersInput = (
    filters: ReviewTaskFilters
): Omit<ReviewTaskFiltersTypeInput, 'bizType' | 'statuses'> => ({
    reviewTaskId: filters.reviewTaskId ? Number(filters.reviewTaskId) : null,
    // 运单号原始字符串透传给后端（audit-service 负责按 ;/,/换行 拆分），仅空白时不下传
    trackingNumbers: filters.trackingNumbers?.trim() || null,
    dspId: filters.dspId ?? null,
    subBizType: filters.bizSubType ?? null,
    // 时间区间统一在转入参处规整为当天 [00:00:00, 23:59:59.999] 再转 ISO（与客诉申诉筛选口径一致）
    submittedAtFrom: filters.submittedAt?.[0]?.startOf('day').toISOString() ?? null,
    submittedAtTo: filters.submittedAt?.[1]?.endOf('day').toISOString() ?? null,
    reviewedAtFrom: filters.reviewedAt?.[0]?.startOf('day').toISOString() ?? null,
    reviewedAtTo: filters.reviewedAt?.[1]?.endOf('day').toISOString() ?? null,
    // bizType 由 Page 层固定写死 CUSTOMER_COMPLAINT（review_task.biz_type 必填）
});

const ReviewTaskPage: React.FC = () => {
    const {t, i18n} = useTranslation();
    // 审核操作权限（提交结论）；查询与导出不受其限制，见 useReviewTaskPermission
    const canOperate = useReviewTaskCanOperate();
    const [filters, setFilters] = useState<ReviewTaskFilters>(getDefaultReviewTaskFilters);
    const [tempFilters, setTempFilters] = useState<ReviewTaskFilters>(filters);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    // 提交时间排序方向；默认 ASC（FIFO，与后端默认一致）
    const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
    const [detailVisible, setDetailVisible] = useState(false);
    // 抽屉只持有 reviewTaskId 数组（单条审核为 [id]，批量审核为选中列表）
    const [drawerTaskIds, setDrawerTaskIds] = useState<number[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
    // 导出：异步生成后弹窗提示去下载中心领取（对齐 exceptionOrder：ParcelsDownloadModal + useDownloadCooldown）
    const [downloadModalVisible, setDownloadModalVisible] = useState(false);
    const [generatedFileName, setGeneratedFileName] = useState('');
    const {downloadLoading, setDownloadLoading, cooldownRemaining, getLastDownloadTime, setLastDownloadTime} =
        useDownloadCooldown(`reviewTaskDownloadLastTime_${getUserInfo()?.account?.id}`);
    const [generateReviewTaskDownloadReport] = useMutation<
        GenerateReviewTaskDownloadReportResponse,
        GenerateReviewTaskDownloadReportVariables
    >(GENERATE_REVIEW_TASK_DOWNLOAD_REPORT);

    const sharedFiltersInput = useMemo(() => buildSharedFiltersInput(filters), [filters]);

    const {data, loading, totalElements, executeQuery} = useReviewTaskQuery(BIZ_TYPE);
    // Tab 计数：传入当前选中的 statuses + 列表 totalElements，
    // 让 hook 跳过必然为 0 的 tab 查询、并在单选 status 时直接复用 totalElements
    const {getCount, refetchAll, loadingAll} = useReviewTaskTabCounts(
        BIZ_TYPE,
        sharedFiltersInput,
        filters.statuses,
        totalElements,
    );

    /**
     * 列表查询的全套筛选/排序必须每次调用时显式传入，否则 setState 异步会导致旧值。
     * sortDir 默认取当前 state，但排序切换时须显式传新值（不能依赖异步的 state）。
     */
    const runListQuery = useCallback(
        (
            sourceFilters: ReviewTaskFilters,
            targetPage: number,
            targetSize: number,
            sortDir: 'ASC' | 'DESC' = sortDirection
        ) => {
            executeQuery(
                buildSharedFiltersInput(sourceFilters),
                sourceFilters.statuses,
                targetPage,
                targetSize,
                sortDir
            );
        },
        [executeQuery, sortDirection]
    );

    const refreshList = useCallback(
        (overridePage?: number) => {
            const targetPage = overridePage ?? page;
            runListQuery(filters, targetPage, pageSize);
        },
        [runListQuery, filters, page, pageSize]
    );

    // 首次加载
    useEffect(() => {
        runListQuery(filters, 1, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** 表头切换提交时间排序：重置到第 1 页，用新方向立即重查 */
    const handleSortChange = (direction: 'ASC' | 'DESC') => {
        setSortDirection(direction);
        setPage(1);
        runListQuery(filters, 1, pageSize, direction);
    };

    const handleSearch = () => {
        setFilters(tempFilters);
        setPage(1);
        runListQuery(tempFilters, 1, pageSize);
        refetchAll();
    };

    const handleReset = () => {
        const defaults = getDefaultReviewTaskFilters();
        setTempFilters(defaults);
        setFilters(defaults);
        setPage(1);
        runListQuery(defaults, 1, pageSize);
        refetchAll();
    };

    const handlePageChange = (nextPage: number, nextSize: number) => {
        if (nextSize !== pageSize) {
            setPage(1);
            setPageSize(nextSize);
            runListQuery(filters, 1, nextSize);
        } else {
            setPage(nextPage);
            runListQuery(filters, nextPage, pageSize);
        }
    };

    const handleOpenDetail = (record: ReviewTaskListItem) => {
        // 单条审核：将当前 ID 包成数组传入抽屉
        setDrawerTaskIds([record.reviewTaskId]);
        setDetailVisible(true);
    };

    const handleBatchReview = () => {
        if (selectedKeys.length === 0) {
            message.warning(t('reviewTask.selectAtLeastOne'));
            return;
        }
        setDrawerTaskIds(selectedKeys);
        setDetailVisible(true);
    };

    const handleCloseDrawer = () => {
        setDetailVisible(false);
        // 关闭后清空抽屉队列与列表选中，避免再次打开时残留旧 ID
        setDrawerTaskIds([]);
        setSelectedKeys([]);
    };

    const handleReviewed = () => {
        refreshList();
        refetchAll();
    };

    /** 导出当前筛选全量（异步生成，去下载中心领取）；用已生效的 filters，补全 bizType + statuses */
    const handleExport = useCallback(async () => {
        const currentTime = Date.now();
        if (currentTime - getLastDownloadTime() < 5000) {
            message.warning(t('reviewTask.export.generating'));
            return;
        }
        if (downloadLoading) {
            message.warning(t('reviewTask.export.generating'));
            return;
        }
        try {
            setDownloadLoading(true);
            setLastDownloadTime(currentTime);
            const exportFilters: ReviewTaskFiltersTypeInput = {
                ...buildSharedFiltersInput(filters),
                bizType: BIZ_TYPE,
                statuses: filters.statuses && filters.statuses.length > 0 ? filters.statuses : null,
            };
            // 后端按 language 出三语表头/文案；取当前 i18n 短码（zh/en/es）
            const language = i18n.language.split('-')[0];
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await generateReviewTaskDownloadReport({
                variables: {filters: exportFilters, language, timezone},
            });
            const result = response.data?.generateReviewTaskDownloadReport;
            if (result?.success && result.fileName) {
                setGeneratedFileName(result.fileName);
                setDownloadModalVisible(true);
            } else if (result?.errorCode?.startsWith('EXCEED_MAX_LIMIT')) {
                const max = result.errorCode.split(':')[1] ?? '';
                message.error(t('reviewTask.export.exceedMaxLimit', {max}));
            } else if (result?.errorCode === 'NO_DATA') {
                message.warning(t('reviewTask.export.noData'));
            } else {
                message.error(t('reviewTask.export.failed'));
            }
        } catch (e) {
            message.error(t('reviewTask.export.failed'));
            setDownloadLoading(false);
        }
    }, [
        downloadLoading,
        getLastDownloadTime,
        setDownloadLoading,
        setLastDownloadTime,
        filters,
        i18n.language,
        generateReviewTaskDownloadReport,
        t,
    ]);

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-3">
            <ReviewTaskTabCards getCount={getCount} loading={loadingAll} />

            <ReviewTaskFilter
                filters={tempFilters}
                onChange={setTempFilters}
                onSearch={handleSearch}
                onReset={handleReset}
                extraActions={
                    <>
                        {/* 无审核操作权限时不渲染批量审核入口（与表格勾选列隐藏一致） */}
                        {canOperate && (
                            <Button
                                type="primary"
                                onClick={handleBatchReview}
                                disabled={selectedKeys.length === 0}
                                className="w-auto min-w-24"
                            >
                                <span className="font-bold">
                                    {t('reviewTask.batchReview')} ({selectedKeys.length})
                                </span>
                            </Button>
                        )}
                        <Button
                            type="primary"
                            onClick={() => void handleExport()}
                            loading={cooldownRemaining > 0}
                            disabled={downloadLoading || cooldownRemaining > 0}
                            className="w-auto min-w-24"
                        >
                            <span className="font-bold">
                                {cooldownRemaining > 0
                                    ? `${t('reviewTask.export.button')} ${cooldownRemaining}s`
                                    : t('reviewTask.export.button')}
                            </span>
                        </Button>
                    </>
                }
            />

            <ReviewTaskTable
                data={data}
                loading={loading}
                onOpenDetail={handleOpenDetail}
                selectedKeys={selectedKeys}
                onSelectChange={setSelectedKeys}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
            />
            <TablePaginationBar
                total={totalElements}
                current={page}
                pageSize={pageSize}
                onChange={handlePageChange}
            />

            <ReviewTaskDetailDrawer
                open={detailVisible}
                reviewTaskIds={drawerTaskIds}
                onClose={handleCloseDrawer}
                onReviewed={handleReviewed}
            />

            <ParcelsDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
            />
        </div>
    );
};

export default ReviewTaskPage;
